import cn from 'classnames'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { ChangeEventHandler, FC, useCallback } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { getFormattedFinance } from 'cng-common/helpers/numbers'
import { Button } from 'core-components/library/button'
import { changeInitiative } from 'partner360/features/QBR'
import { IQbrDocumentInitiative } from 'partner360/models/qbr/iQbrDocumentInitiative'

import { TRANSLATION_KEYS } from '../../translations'

import { Incremental } from './components/incremental/Incremental'
import { Period } from './components/period/Period'
import { Status } from './components/status/Status'
import { TextareaField } from './components/textareaField/TextareaField'

import styles from './InitiativeCard.module.scss'

interface IInitiativeCardProps {
  initiativeData: IQbrDocumentInitiative
  onInitiativeRemove: (initiativeId: number) => void
}

export const InitiativeCard: FC<IInitiativeCardProps> = ({
  initiativeData,
  onInitiativeRemove,
}) => {
  const dispatch = useAppDispatch()

  const handleRemoveInitiative = useCallback(() => {
    onInitiativeRemove(initiativeData.EntryId)
  }, [initiativeData.EntryId, onInitiativeRemove])

  const handleChangeCurrentProgress = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    (e) => {
      dispatch(
        changeInitiative({
          ...initiativeData,
          CurrentProgress: e.target.value,
        })
      )
    },
    [dispatch, initiativeData]
  )

  const handleProceedToInitiatives = useCallback(() => {
    window.open(
      `/page/jbl-hpscoutso/pid/${initiativeData.PartnerId}/jbp-tab/21/fyear/${initiativeData.FYear}/ptype/${initiativeData.Offering}`,
      '_blank'
    )
  }, [initiativeData])

  const handleChangeFollowUp = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    (e) => {
      dispatch(
        changeInitiative({
          ...initiativeData,
          FollowUp: e.target.value,
        })
      )
    },
    [dispatch, initiativeData]
  )

  const handleChangeInitiativeStatus = useCallback(
    (newStatus: string) => {
      dispatch(
        changeInitiative({
          ...initiativeData,
          Status: newStatus,
        })
      )
    },
    [dispatch, initiativeData]
  )

  return (
    <div className={styles.cardContainer}>
      <div className={styles.cardHeader}>
        <div className={styles.partnerData}>
          <div className={styles.titleContainer}>
            <div className={styles.partnerNameLabel}>{initiativeData.PartnerName}</div>
          </div>
          <div className={styles.detailsContainer}>
            <div className={styles.partnerIdContainer}>
              <div className={styles.fieldLabel}>PID</div>
              <div className={styles.fieldValue}>{initiativeData.PartnerId}</div>
            </div>
            <div className={styles.locationIdContainer}>
              <div className={styles.fieldLabel}>Location ID</div>
              <div className={styles.fieldValue}>{initiativeData.LocationID}</div>
            </div>
          </div>
        </div>

        <div className={styles.cardActions}>
          <Button className={styles.removeAction} onClick={handleRemoveInitiative}>
            <Translation
              span
              htmlAvailable
              text={TRANSLATION_KEYS.initiativeCard.removeFromQbrButton}
            />
          </Button>
          <div className={styles.initiativesLink} onClick={handleProceedToInitiatives}>
            <Translation
              span
              htmlAvailable
              text={TRANSLATION_KEYS.initiativeCard.initiativesLink}
            />
            {'>>'}
          </div>
        </div>
      </div>

      <div className={styles.cardContent}>
        <div className={styles.cardSubHeader}>
          <div className={styles.offeringWrapper}>
            <div className={styles.fieldLabel}>
              <Translation span htmlAvailable text={TRANSLATION_KEYS.initiativeCard.offering} />
            </div>
            <div className={styles.fieldValue}>{initiativeData.Offering}</div>
            <div className={styles.businessUnitWrapper}>
              <div className={styles.fieldLabel}>
                <Translation
                  span
                  htmlAvailable
                  text={TRANSLATION_KEYS.initiativeCard.businessUnit}
                />
              </div>
              <div className={styles.fieldValue}>
                {initiativeData.BusinessUnit && (
                  <Translation span htmlAvailable text={initiativeData.BusinessUnit} />
                )}
              </div>
            </div>
          </div>
          <div className={styles.objectiveWrapper}>
            <div className={styles.fieldLabel}>
              <Translation span htmlAvailable text={TRANSLATION_KEYS.initiativeCard.keyObjective} />
            </div>
            <div className={styles.fieldValue}>{initiativeData.KeyObjective}</div>
          </div>
          <div className={styles.statusAndOwnerWrapper}>
            <div className={styles.fieldLabel} />
            <div className={styles.fieldValue}>
              {initiativeData.Owner && (
                <Translation
                  span
                  htmlAvailable
                  text={TRANSLATION_KEYS.initiativeCard.byOwner}
                  replaceParams={[
                    {
                      value: initiativeData.Owner,
                      key: '{owner}',
                    },
                  ]}
                />
              )}
            </div>
            <div className={styles.statusWrapper}>
              <Status value={initiativeData.Status} onClick={handleChangeInitiativeStatus} />
            </div>
          </div>
        </div>
        <div className={styles.cardMiddle}>
          <div className={styles.nameAndDescriptionWrapper}>
            <div className={styles.name}>{initiativeData.Name}</div>
            <div className={styles.description}>{initiativeData.Description}</div>
          </div>
          <div className={styles.financialsWrapper}>
            <div className={styles.goalWrapper}>
              <div className={styles.fieldLabel}>
                <Translation span htmlAvailable text={TRANSLATION_KEYS.initiativeCard.goal} />
              </div>
              <div className={cn(styles.fieldValue, styles.fieldValue__alignRight)}>
                {typeof initiativeData.Goal === 'number'
                  ? getFormattedFinance(initiativeData.Goal, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  : '-'}
              </div>
            </div>
            <div className={styles.incrementalWrapper}>
              <div className={styles.fieldLabel}>Incremental</div>
              <div className={cn(styles.fieldValue, styles.fieldValue__alignRight)}>
                <Incremental value={initiativeData.Incremental} />
              </div>
            </div>
            <div className={styles.hpInvestmentWrapper}>
              <div className={styles.hpInvestment}>
                {initiativeData.HPInvestmentSum && (
                  <>
                    <div className={cn(styles.fieldValue, styles.fieldValue__alignRight)}>
                      <Translation text={TRANSLATION_KEYS.initiativeCard.fundsNeeded} />
                      <span>
                        {getFormattedFinance(initiativeData.HPInvestmentSum, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </span>
                      <span>
                        {' '}
                        <Translation span text={TRANSLATION_KEYS.initiativeCard.from} />{' '}
                      </span>
                      <span>{initiativeData.HPInvestmentSource}</span>
                    </div>
                  </>
                )}
                {!initiativeData.HPInvestmentSum && (
                  <Translation
                    span
                    htmlAvailable
                    text={TRANSLATION_KEYS.initiativeCard.noFundsNeeded}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.cardBottom}>
          <div className={styles.periodWrapper}>
            <Period period={initiativeData.Period} />
          </div>
          <div className={styles.currentProgress}>
            <TextareaField
              placeholderTranslationKey={TRANSLATION_KEYS.initiativeCard.currentProgressPlaceholder}
              value={initiativeData.CurrentProgress}
              onChange={handleChangeCurrentProgress}
            />
          </div>
          <div className={styles.followUp}>
            <TextareaField
              placeholderTranslationKey={TRANSLATION_KEYS.initiativeCard.followUp}
              value={initiativeData.FollowUp}
              onChange={handleChangeFollowUp}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
